import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart3, Download, Loader2, TrendingUp, Bike, Gauge, Euro, ArrowUpDown, Info,
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const eur = (v) => `€ ${Number(v || 0).toFixed(2)}`;
const num = (v) => Number(v || 0).toLocaleString("it-IT");
const CANALE_LABEL = { online: "Online", interno: "Interno/Backoffice" };
const PROV_LABEL = { bonifico: "Bonifico", satispay: "Satispay", nexi: "Nexi", paypal: "PayPal", in_sede: "In sede (contanti)" };
const iso = (d) => d.toISOString().slice(0, 10);

const KPI = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-sm border border-zinc-800 bg-[#141414] p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-500 text-[11px] uppercase tracking-wider"><Icon className="h-3.5 w-3.5 text-[#f98125]" /> {label}</div>
    <div className="font-display text-3xl mt-1 text-white">{value}</div>
  </div>
);

export default function Statistics() {
  const today = new Date();
  const d30 = new Date(today.getTime() - 29 * 86400000);
  const [dateFrom, setDateFrom] = useState(iso(d30));
  const [dateTo, setDateTo] = useState(iso(today));
  const [categoria, setCategoria] = useState("all");
  const [taglia, setTaglia] = useState("all");
  const [canale, setCanale] = useState("all");
  const [provider, setProvider] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sortKey, setSortKey] = useState("ricavi");
  const [sortDir, setSortDir] = useState("desc");

  const params = () => {
    const p = { date_from: dateFrom, date_to: dateTo };
    if (categoria !== "all") p.categoria = categoria;
    if (taglia !== "all") p.taglia = taglia;
    if (canale !== "all") p.canale = canale;
    if (provider !== "all") p.provider = provider;
    return p;
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/statistics/rentals", { params: params() });
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel caricamento statistiche"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo, categoria, taglia, canale, provider]);

  const doExport = async (formato) => {
    setBusy(true);
    try {
      const res = await api.post("/statistics/export", { ...params(), formato }, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `statistiche_${dateFrom}_${dateTo}.${formato === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`Export ${formato.toUpperCase()} pronto`);
    } catch (e) { toast.error("Export non riuscito"); }
    finally { setBusy(false); }
  };

  const sortedBici = useMemo(() => {
    if (!data?.bici) return [];
    const arr = [...data.bici];
    arr.sort((a, b) => {
      const va = sortKey === "numero" ? (a.numero || "") : (a[sortKey] || 0);
      const vb = sortKey === "numero" ? (b.numero || "") : (b[sortKey] || 0);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return arr;
  }, [data, sortKey, sortDir]);

  const setSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("desc"); } };

  const opzioni = data?.opzioni || { categorie: [], taglie: [] };
  const tot = data?.totali || {};
  const serieData = (data?.serie_ricavi || []).map((s) => ({ name: s.periodo, ricavi: s.ricavi }));
  const topRicavi = (data?.top_bici_ricavi || []).map((b) => ({ name: b.numero, ricavi: b.ricavi }));

  const Th = ({ k, children, right }) => (
    <th onClick={() => setSort(k)} data-testid={`stats-sort-${k}`} className={`px-3 py-2 cursor-pointer select-none hover:text-white ${right ? "text-right" : "text-left"}`}>
      <span className="inline-flex items-center gap-1">{children}<ArrowUpDown className={`h-3 w-3 ${sortKey === k ? "text-[#f98125]" : "text-zinc-700"}`} /></span>
    </th>
  );

  const Agg = ({ title, rows, keyLabel }) => (
    <div className="rounded-sm border border-zinc-800 bg-[#141414] p-4" data-testid={`stats-agg-${title}`}>
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">{title}</div>
      <div className="space-y-1.5 max-h-52 overflow-y-auto">
        {rows.length === 0 ? <div className="text-sm text-zinc-600">Nessun dato</div> : rows.map((r) => (
          <div key={r.chiave} className="flex items-center justify-between text-sm">
            <span className="text-zinc-300 truncate">{keyLabel ? (keyLabel[r.chiave] || r.chiave) : r.chiave}</span>
            <span className="text-white font-medium tabular-nums">{eur(r.ricavi)} <span className="text-zinc-600 text-xs">· {r.n_noleggi}</span></span>
          </div>
        ))}
      </div>
    </div>
  );

  const canaleRows = data ? Object.entries(data.per_canale).map(([k, v]) => ({ chiave: k, ...v })) : [];

  return (
    <div className="space-y-6" data-testid="statistics-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight flex items-center gap-3"><BarChart3 className="h-8 w-8 text-[#f98125]" /> Statistiche</h1>
          <p className="text-zinc-500 text-sm mt-1">Ricavi e utilizzo per bici · la fatturazione resta esterna</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => doExport("csv")} disabled={busy || loading} data-testid="stats-export-csv" className="flex items-center gap-2 rounded-sm border border-zinc-800 px-4 py-2.5 text-sm text-zinc-200 hover:border-[#f98125]/60 disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} CSV</button>
          <button onClick={() => doExport("xlsx")} disabled={busy || loading} data-testid="stats-export-xlsx" className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold px-4 py-2.5 text-sm hover:bg-[#d96a12] disabled:opacity-50">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} XLSX</button>
        </div>
      </div>

      {/* Filtri */}
      <div className="rounded-sm border border-zinc-800 bg-[#141414] p-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Dal</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="stats-date-from" className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Al</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="stats-date-to" className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Categoria</Label>
          <Select value={categoria} onValueChange={setCategoria}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="stats-categoria"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte</SelectItem>{opzioni.categorie.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Taglia</Label>
          <Select value={taglia} onValueChange={setTaglia}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="stats-taglia"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte</SelectItem>{opzioni.taglie.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Prenotazione</Label>
          <Select value={canale} onValueChange={setCanale}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="stats-canale"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutte</SelectItem><SelectItem value="online">Online</SelectItem><SelectItem value="interno">Interno/Backoffice</SelectItem></SelectContent></Select></div>
        <div><Label className="text-[10px] uppercase tracking-wider text-zinc-500">Pagamento</Label>
          <Select value={provider} onValueChange={setProvider}><SelectTrigger className="mt-1 bg-[#0A0A0A] border-zinc-800 rounded-sm h-9" data-testid="stats-provider"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="all">Tutti</SelectItem><SelectItem value="bonifico">Bonifico</SelectItem><SelectItem value="satispay">Satispay</SelectItem><SelectItem value="nexi">Nexi</SelectItem><SelectItem value="paypal">PayPal</SelectItem></SelectContent></Select></div>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#f98125]" /></div>
      ) : !data ? null : (
        <>
          {/* KPI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI icon={Euro} label="Ricavi periodo" value={eur(tot.ricavi)} testid="kpi-ricavi" />
            <KPI icon={Bike} label="Noleggi" value={num(tot.n_noleggi)} testid="kpi-noleggi" />
            <KPI icon={Gauge} label="Km percorsi" value={num(tot.km)} testid="kpi-km" />
            <KPI icon={TrendingUp} label="Bici attive" value={num(tot.bici_count)} testid="kpi-bici" />
          </div>

          {/* Grafici */}
          <div className="grid md:grid-cols-2 gap-3">
            <div className="rounded-sm border border-zinc-800 bg-[#141414] p-4" data-testid="stats-chart-ricavi">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Ricavi per periodo <span className="text-zinc-600">({data.serie_granularita === "day" ? "giorno" : data.serie_granularita === "week" ? "settimana" : "mese"})</span></div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={serieData}><CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#27272a" }} />
                  <YAxis tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 4 }} labelStyle={{ color: "#a1a1aa" }} formatter={(v) => eur(v)} cursor={{ fill: "rgba(249,129,37,0.08)" }} />
                  <Bar dataKey="ricavi" fill="#f98125" radius={[3, 3, 0, 0]} /></BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-sm border border-zinc-800 bg-[#141414] p-4" data-testid="stats-chart-top">
              <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Top bici per ricavi</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={topRicavi} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
                  <XAxis type="number" tick={{ fill: "#71717a", fontSize: 10 }} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 11 }} tickLine={false} axisLine={{ stroke: "#27272a" }} width={50} />
                  <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272a", borderRadius: 4 }} formatter={(v) => eur(v)} cursor={{ fill: "rgba(249,129,37,0.08)" }} />
                  <Bar dataKey="ricavi" radius={[0, 3, 3, 0]}>{topRicavi.map((_, i) => <Cell key={i} fill={i === 0 ? "#f98125" : "#3f3f46"} />)}</Bar></BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Riepiloghi aggregati */}
          <div className="grid md:grid-cols-4 gap-3">
            <Agg title="Per categoria" rows={data.per_categoria} />
            <Agg title="Per taglia" rows={data.per_taglia} />
            <Agg title="Per prenotazione" rows={canaleRows} keyLabel={CANALE_LABEL} />
            <Agg title="Per pagamento" rows={data.per_pagamento} keyLabel={PROV_LABEL} />
          </div>

          {/* Tabella per bici */}
          <div className="rounded-sm border border-zinc-800 bg-[#141414] overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800 text-xs uppercase tracking-wider text-zinc-500">Dettaglio per bici ({sortedBici.length})</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="stats-table">
                <thead className="text-[11px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  <tr><Th k="numero">Bici</Th><th className="px-3 py-2 text-left">Modello</th><th className="px-3 py-2 text-left">Categoria</th><th className="px-3 py-2 text-left">Taglia</th>
                    <Th k="n_noleggi" right>Noleggi</Th><Th k="km" right>Km</Th><Th k="ricavi" right>Ricavi</Th><Th k="tasso_utilizzo" right>Utilizzo %</Th></tr>
                </thead>
                <tbody>
                  {sortedBici.length === 0 ? (
                    <tr><td colSpan={8} className="px-3 py-10 text-center text-zinc-600" data-testid="stats-empty">Nessun dato per i filtri selezionati.</td></tr>
                  ) : sortedBici.map((b) => (
                    <tr key={b.bike_id} data-testid={`stats-row-${b.bike_id}`} className="border-b border-zinc-800/50 hover:bg-white/[0.02]">
                      <td className="px-3 py-2 font-display text-base text-[#f98125]">{b.numero}</td>
                      <td className="px-3 py-2 text-zinc-400">{b.modello || "—"}</td>
                      <td className="px-3 py-2 text-zinc-400">{b.categoria_nome || "—"}</td>
                      <td className="px-3 py-2 text-zinc-400">{b.taglia || "—"}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{num(b.n_noleggi)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{num(b.km)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-white font-medium">{eur(b.ricavi)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{b.tasso_utilizzo}%</td>
                    </tr>
                  ))}
                </tbody>
                {sortedBici.length > 0 && (
                  <tfoot className="border-t-2 border-zinc-700 font-semibold" data-testid="stats-totals">
                    <tr><td className="px-3 py-2.5" colSpan={4}>TOTALE</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{num(tot.n_noleggi)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{num(tot.km)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-[#f98125]">{eur(tot.ricavi)}</td>
                      <td className="px-3 py-2.5"></td></tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Criteri */}
          <div className="rounded-sm border border-zinc-800/60 bg-[#0d0d0d] p-4 text-xs text-zinc-500 flex gap-2" data-testid="stats-criteri">
            <Info className="h-4 w-4 text-zinc-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div><b className="text-zinc-400">Criterio ricavi:</b> l'importo di ogni noleggio è diviso in quota uguale tra le bici incluse (es. €150 con 3 bici = €50 a bici).</div>
              <div><b className="text-zinc-400">Utilizzo:</b> giorni-noleggio occupati ÷ giorni del periodo ({data.periodo.giorni} giorni). Indicatore approssimato.</div>
              <div><b className="text-zinc-400">Noleggi inclusi:</b> prenotazioni non annullate con data di inizio nel periodo. Km dai check-out/check-in.</div>
              <div><b className="text-zinc-400">Pagamento:</b> dai dati transazioni. I noleggi incassati solo in sede (senza transazione) sono raggruppati come "In sede" ed esclusi dal filtro per singolo provider.</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
